import { useCallback } from 'react'
import { assign, newProject, type Project } from '../domain/projects'
import { usePersistentState } from './viewState'

/**
 * Projects defined in this session.
 *
 * Kept with the rest of the session rather than as a preference: a grouping is
 * part of what you assembled, so it persists across visits and travels in an
 * exported session file. Someone opening that file sees the same projects you
 * did, which is the point of sharing one.
 */

export interface ProjectsState {
  projects: Project[]
  restored: boolean
  create: (name: string) => void
  rename: (id: string, name: string) => void
  remove: (id: string) => void
  put: (softwareKey: string, projectId: string | null) => void
  replaceAll: (projects: Project[]) => void
}

export function useProjects(): ProjectsState {
  const [projects, setProjects, restored] = usePersistentState<Project[]>('projects', [], {
    validate: (v) =>
      Array.isArray(v) &&
      v.every(
        (p) =>
          typeof p?.id === 'string' &&
          typeof p.name === 'string' &&
          Array.isArray(p.members) &&
          p.members.every((m: unknown) => typeof m === 'string'),
      ),
  })

  const create = useCallback(
    (name: string) => {
      const trimmed = name.trim()
      if (!trimmed) return
      // Made outside the update, so replaying it after the restore keeps one id.
      const project = newProject(trimmed)
      setProjects((list) => [...list, project])
    },
    [setProjects],
  )

  const rename = useCallback(
    (id: string, name: string) => {
      const trimmed = name.trim()
      if (!trimmed) return
      setProjects((list) =>
        list.map((project) => (project.id === id ? { ...project, name: trimmed } : project)),
      )
    },
    [setProjects],
  )

  const remove = useCallback(
    (id: string) => {
      // Removing a project releases its software rather than removing it: the
      // reports are still loaded and belong to the session.
      setProjects((list) => list.filter((project) => project.id !== id))
    },
    [setProjects],
  )

  const put = useCallback(
    (softwareKey: string, projectId: string | null) => {
      setProjects((list) => assign(list, softwareKey, projectId))
    },
    [setProjects],
  )

  return { projects, restored, create, rename, remove, put, replaceAll: setProjects }
}
